import React from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { NotificationGroup } from '../services/dataService';
import { getNotificationMeta } from '../src/lib/notificationCatalog';
import { getNotificationIcon, getNotificationColorClasses } from '../src/lib/notificationIcons';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatRelativeTime = (iso: string, locale: string): string => {
  const ts = new Date(iso || 0).getTime();
  if (!Number.isFinite(ts)) return '';
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return locale === 'en' ? 'just now' : 'justo ahora';
  if (diffMin < 60) return locale === 'en' ? `${diffMin}m ago` : `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return locale === 'en' ? `${diffH}h ago` : `hace ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  return locale === 'en' ? `${diffD}d ago` : `hace ${diffD} d`;
};

interface NotificationGroupRowProps {
  group: NotificationGroup;
  locale: string;
  onClick: () => void;
  onArchiveToggle?: (e: React.MouseEvent) => void;
  compact?: boolean;
}

// Fila de notificación compartida entre NotificationBell.tsx (dropdown) y Notificaciones.tsx
// (página dedicada) -- antes duplicada casi idéntica en ambos. Ícono+color resueltos en vivo
// desde notificationCatalog.ts por `tipo` (group.prioridad es solo el respaldo histórico si el
// tipo ya no existe en el catálogo, ver server.ts).
const NotificationGroupRow: React.FC<NotificationGroupRowProps> = ({ group, locale, onClick, onArchiveToggle, compact }) => {
  const { t } = useTranslation();
  const meta = getNotificationMeta(group.tipo);
  const Icon = getNotificationIcon(meta.iconName);
  const colors = getNotificationColorClasses(meta.colorClass);

  return (
    <div
      data-testid="notification-item"
      className={cn(
        'group relative w-full text-left border-b border-slate-50 dark:border-slate-800/60 last:border-b-0 transition-colors',
        compact ? 'px-4 py-3' : 'px-5 py-4',
        group.unreadCount > 0
          ? 'bg-blue-50/60 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/40'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
      )}
    >
      <button onClick={onClick} className="w-full text-left cursor-pointer">
        <div className="flex items-start gap-3">
          <span className={cn('mt-0.5 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ring-1', colors.bg, colors.ring)}>
            <Icon size={14} className={colors.text} />
          </span>
          <div className="min-w-0 flex-1 pr-6">
            <div className="flex items-center gap-1.5">
              {group.unreadCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-brand-600 dark:bg-blue-400 shrink-0" />}
              <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{group.titulo}</p>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">{group.mensaje}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-slate-400">{formatRelativeTime(group.fecha_creacion, locale)}</span>
              {group.count > 1 && (
                <span className="text-[10px] font-bold text-slate-400">
                  &middot; {group.count} {t('notifications.unreadSuffix')}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
      {onArchiveToggle && (
        <button
          onClick={onArchiveToggle}
          aria-label={group.archivada ? t('notifications.unarchive') : t('notifications.archive')}
          data-tooltip={group.archivada ? t('notifications.unarchive') : t('notifications.archive')}
          className="absolute right-3 top-3 p-1.5 rounded-lg text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 hover:text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
        >
          {group.archivada ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </button>
      )}
    </div>
  );
};

export default NotificationGroupRow;
